import * as ngCore from '@angular/core'
import type * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as rxjs from 'rxjs'

import * as dms from '../../../dms'
import * as organizations from '../../organizations'

import * as gql from './gql'
import type { TypeParams } from './types'

/**
 * Unassigned sponsored accounts API.
 */
@ngCore.Injectable()
export class Api implements ec.models.Api<TypeParams> {
  /** @see {@link dms.schema.Feature} */
  private readonly _schemaFeature = ngCore.inject(dms.schema.Feature)

  /** @see {@link organizations.Feature} */
  private readonly _organizationsFeature = ngCore.inject(organizations.Feature)

  /** Organization entities in the schema, in hierarchy order. */
  private readonly _organizationEntities$ =
    this._schemaFeature.selectors.schema.asObservable.pipe(
      rxjs.map(schema =>
        schema.getEntitiesInDimension(organizations.constants.DIMENSION_ID),
      ),
    )

  /** Every known organization, keyed by id, across all levels. */
  private readonly _organizationLookup$ = this._organizationEntities$.pipe(
    rxjs.switchMap(entities =>
      entities.length > 0
        ? rxjs.combineLatest(
            entities.map(entity =>
              this._organizationsFeature.selectors
                .getOrganizationEc(entity.id)
                .asObservable.pipe(
                  rxjs.switchMap(
                    orgEc =>
                      orgEc?.plugins.cachePlugin.feature.selectors.lookup
                        .asObservable ?? rxjs.of(undefined),
                  ),
                ),
            ),
          )
        : rxjs.of([]),
    ),
    rxjs.map(lookups =>
      lookups.reduce<
        Readonly<
          Record<
            ents.extra.space.Organization.Id,
            ents.extra.space.Organization
          >
        >
      >((all, lookup) => ({ ...all, ...lookup }), {}),
    ),
  )

  /**
   * Serves the sponsored payrolls whose person occupies no Space record, which
   * is what carries the accounts this report is about.
   *
   * @inheritdoc
   */
  public list = ec.models.Api.list.using.everything<TypeParams>(
    (context, { filter, pagination, sort }) =>
      context
        ? gql.queries.payrolls.executor({
            ...context,
            filter: gql.inputs.filter.TRANSFORMER.getGqlFrom(
              filter?.additionalFilters,
            ),
            pagination,
            sort: sort && { by: sort.by, descending: sort.descending },
          })
        : rxjs.of([]),
  )

  /** @inheritdoc */
  public count: ec.models.Api<TypeParams>['count'] = (context, request) =>
    context
      ? gql.queries.payrollsCount.executor({
          ...context,
          filter: gql.inputs.filter.TRANSFORMER.getGqlFrom(
            request?.filter?.additionalFilters,
          ),
        })
      : rxjs.of(0)

  /**
   * Saves the comment a survey user left on one account.
   *
   * @inheritdoc
   */
  public update: ec.models.Api<TypeParams>['update'] = (
    context,
    { id, updates },
  ) =>
    context
      ? gql.mutations.updateComment.executor({
          ...context,
          id,
          text: updates.comment ?? null,
        })
      : rxjs.EMPTY

  /**
   * The account's own code and name, then a column for every organization
   * entity in the schema (e.g. Division, Department), read straight off the
   * payroll's organization lookup — which already carries the whole ancestry,
   * not just the level the account is aligned at.
   *
   * @inheritdoc
   */
  public getAttributes: ec.models.Api<TypeParams>['getAttributes'] = () =>
    rxjs
      .combineLatest({
        organizationEntities: this._organizationEntities$,
        organizationLookup: this._organizationLookup$,
      })
      .pipe(
        rxjs.map(({ organizationEntities, organizationLookup }) => [
          {
            getValueFrom: ({ project }) => project?.code,
            id: 'projectCode',
            name: 'Project Code',
          },
          {
            getValueFrom: ({ project }) => project?.name,
            id: 'projectName',
            name: 'Project Name',
          },
          ...organizationEntities.map<ec.models.Attribute<TypeParams>>(
            entity => ({
              getValueFrom: ({ organization }) => {
                const organizationId = organization[entity.id]
                return organizationId
                  ? organizationLookup[organizationId]?.name
                  : undefined
              },
              id: entity.id,
              name: entity.name,
            }),
          ),
          ec.models.Attribute.fromKey<TypeParams>('comment', {
            name: 'Comment',
          }),
        ]),
      )
}
